import { FC, useState, useEffect, useRef } from "react";
import { C, BEAST_QUOTES } from "../constants";
import { Ic } from "./Icons";
import { View } from "../types";
import { FULL_VERSION } from "../buildInfo";
import { useTheme } from "../theme";
import { useLocation, useNavigate } from "react-router-dom";

type NavItem = { label: string; icon: string; view: View; beta?: boolean };

const NAV: NavItem[] = [
  { label:"Recipes",          icon:"layers", view:"dashboard"        },
  { label:"Content",          icon:"folder", view:"content"          },
  { label:"Launcher",         icon:"play",   view:"launcher"         },
  { label:"Launcher Tracker", icon:"clock",  view:"launcher_tracker" },
  { label:"Ad Copy Review",   icon:"check",  view:"ad_copy_review"   },
  { label:"Performance",      icon:"bar",    view:"performance"      },
  { label:"Social Brand",     icon:"dollar", view:"internal_report", beta:true },
  { label:"Auto Rules",       icon:"zap",    view:"rules"            },
  { label:"Creator Management", icon:"tag",  view:"creators_admin"   },
  { label:"Partner Network",  icon:"db",     view:"partner_network"  },
  { label:"Client Relations", icon:"users",  view:"creator_crm"      },
];
// URL Creator is not a top-level nav section — it's a modal opened from inside Launcher, not a
// routed page.

// Hidden — keep commented for future re-enable:
// { label:"Run History", icon:"file",  view:"runs"      },
// { label:"Analytics",   icon:"bar",   view:"analytics" },
// { label:"Team",        icon:"users", view:"team"      },

type Props = {
  view: View;
  onNavigate: (v: View) => void;
  currentUser?: string;
  onSignOut?: () => void;
  isAdmin?: boolean;
  canView?: (v: View) => boolean;
};

export const Sidebar: FC<Props> = ({ view, onNavigate, currentUser, onSignOut, isAdmin, canView }) => {
  // Show only the pages this user's roles grant (admin sees all). Missing canView → show all
  // (defensive default; AppShell always passes it).
  const navItems = NAV.filter(({ view: v }) => (canView ? canView(v) : true));
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [quoteIdx, setQuoteIdx] = useState(0);
  const [pulseIntensity, setPulseIntensity] = useState(0);
  const cortexRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const i = setInterval(() => setQuoteIdx(q => (q + 1) % BEAST_QUOTES.length), 3500);
    return () => clearInterval(i);
  }, []);

  // Mouse proximity drives pulse intensity
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (!cortexRef.current) return;
      const r = cortexRef.current.getBoundingClientRect();
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      const dist = Math.hypot(e.clientX - cx, e.clientY - cy);
      const intensity = Math.max(0, Math.min(1, 1 - dist / 400));
      setPulseIntensity(intensity);
    };
    window.addEventListener("mousemove", fn);
    return () => window.removeEventListener("mousemove", fn);
  }, []);

  return (
    <aside style={{ width:210, background:C.panel, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", flexShrink:0, overflow:"auto", position:"sticky", top:0, height:"100vh" }}>

      <div style={{ padding:"18px 16px", borderBottom:`1px solid ${C.border}` }}>
        <div ref={cortexRef} style={{ display:"flex", alignItems:"center", gap:12 }}>
          {/* Neural Orb */}
          <div style={{
            width: 40, height: 40, borderRadius: "50%", position: "relative", flexShrink: 0,
            background: `radial-gradient(circle at 38% 35%, rgba(139,92,246,${0.25 + pulseIntensity * 0.35}), rgba(99,102,241,${0.12 + pulseIntensity * 0.2}), transparent 70%)`,
            boxShadow: `0 0 ${18 + pulseIntensity * 25}px rgba(139,92,246,${0.3 + pulseIntensity * 0.45}), inset 0 0 ${10 + pulseIntensity * 12}px rgba(139,92,246,${0.2 + pulseIntensity * 0.25})`,
            border: `1.5px solid rgba(139,92,246,${0.3 + pulseIntensity * 0.4})`,
            transition: "box-shadow 0.2s, border-color 0.2s",
          }}>
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ position: "absolute", inset: 0 }}>
              {/* Outer ring */}
              <circle cx="20" cy="20" r="18" stroke={`rgba(139,92,246,${0.2 + pulseIntensity * 0.25})`} strokeWidth="0.7" fill="none" />
              {/* Orbiting particles */}
              <circle r="2" fill={`rgba(167,139,250,${0.7 + pulseIntensity * 0.3})`}>
                <animateMotion dur="3s" repeatCount="indefinite" path="M20,2 A18,18 0 1,1 19.99,2" />
              </circle>
              <circle r="1.5" fill={`rgba(99,102,241,${0.6 + pulseIntensity * 0.4})`}>
                <animateMotion dur="4.5s" repeatCount="indefinite" path="M38,20 A18,18 0 1,1 37.99,20" />
              </circle>
              <circle r="1.2" fill={`rgba(192,132,252,${0.5 + pulseIntensity * 0.5})`}>
                <animateMotion dur="6s" repeatCount="indefinite" path="M20,38 A18,18 0 1,1 20.01,38" />
              </circle>
              {/* Inner neural web */}
              <path d="M14,14 Q20,11 26,14" stroke={`rgba(139,92,246,${0.35 + pulseIntensity * 0.55})`} strokeWidth="0.8" fill="none" strokeLinecap="round">
                <animate attributeName="d" values="M14,14 Q20,11 26,14;M14,14 Q20,9 26,14;M14,14 Q20,11 26,14" dur="2s" repeatCount="indefinite" />
              </path>
              <path d="M12,20 Q20,17 28,20" stroke={`rgba(167,139,250,${0.3 + pulseIntensity * 0.5})`} strokeWidth="0.7" fill="none" strokeLinecap="round">
                <animate attributeName="d" values="M12,20 Q20,17 28,20;M12,20 Q20,23 28,20;M12,20 Q20,17 28,20" dur="2.5s" repeatCount="indefinite" />
              </path>
              <path d="M14,26 Q20,23 26,26" stroke={`rgba(99,102,241,${0.25 + pulseIntensity * 0.45})`} strokeWidth="0.7" fill="none" strokeLinecap="round">
                <animate attributeName="d" values="M14,26 Q20,23 26,26;M14,26 Q20,28 26,26;M14,26 Q20,23 26,26" dur="3s" repeatCount="indefinite" />
              </path>
              {/* Neural nodes — pulse with proximity */}
              <circle cx="16" cy="14" r={1.5 + pulseIntensity * 1} fill={`rgba(167,139,250,${0.55 + pulseIntensity * 0.45})`}>
                <animate attributeName="opacity" values="0.5;1;0.5" dur="1.2s" repeatCount="indefinite" />
              </circle>
              <circle cx="24" cy="14" r={1.2 + pulseIntensity * 0.9} fill={`rgba(139,92,246,${0.5 + pulseIntensity * 0.5})`}>
                <animate attributeName="opacity" values="0.45;0.95;0.45" dur="1.6s" repeatCount="indefinite" />
              </circle>
              <circle cx="20" cy="20" r={2 + pulseIntensity * 1.2} fill={`rgba(192,132,252,${0.6 + pulseIntensity * 0.4})`}>
                <animate attributeName="opacity" values="0.55;1;0.55" dur="1s" repeatCount="indefinite" />
              </circle>
              <circle cx="14" cy="22" r={1 + pulseIntensity * 0.8} fill={`rgba(99,102,241,${0.45 + pulseIntensity * 0.45})`}>
                <animate attributeName="opacity" values="0.4;0.9;0.4" dur="1.8s" repeatCount="indefinite" />
              </circle>
              <circle cx="26" cy="22" r={1.2 + pulseIntensity * 0.9} fill={`rgba(139,92,246,${0.5 + pulseIntensity * 0.5})`}>
                <animate attributeName="opacity" values="0.45;1;0.45" dur="1.4s" repeatCount="indefinite" />
              </circle>
              {/* Connection lines between nodes */}
              <line x1="16" y1="14" x2="20" y2="20" stroke={`rgba(139,92,246,${0.15 + pulseIntensity * 0.3})`} strokeWidth="0.6" />
              <line x1="24" y1="14" x2="20" y2="20" stroke={`rgba(167,139,250,${0.15 + pulseIntensity * 0.28})`} strokeWidth="0.6" />
              <line x1="14" y1="22" x2="20" y2="20" stroke={`rgba(99,102,241,${0.12 + pulseIntensity * 0.25})`} strokeWidth="0.5" />
              <line x1="26" y1="22" x2="20" y2="20" stroke={`rgba(192,132,252,${0.12 + pulseIntensity * 0.25})`} strokeWidth="0.5" />
              <line x1="16" y1="14" x2="24" y2="14" stroke={`rgba(139,92,246,${0.1 + pulseIntensity * 0.2})`} strokeWidth="0.5" />
              <line x1="14" y1="22" x2="26" y2="22" stroke={`rgba(167,139,250,${0.1 + pulseIntensity * 0.2})`} strokeWidth="0.5" />
              {/* Electric spark flashes — only on close proximity */}
              {pulseIntensity > 0.2 && (
                <>
                  <circle cx="18" cy="17" r="0.5" fill="rgba(255,255,255,0.9)">
                    <animate attributeName="r" values="0;2;0" dur="0.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;1;0" dur="0.6s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="22" cy="18" r="0.5" fill="rgba(255,255,255,0.85)">
                    <animate attributeName="r" values="0;1.8;0" dur="0.8s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;0.9;0" dur="0.8s" repeatCount="indefinite" />
                  </circle>
                </>
              )}
              {pulseIntensity > 0.5 && (
                <circle cx="20" cy="20" r="0.5" fill="rgba(255,255,255,1)">
                  <animate attributeName="r" values="0;2.5;0" dur="0.5s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;1;0" dur="0.5s" repeatCount="indefinite" />
                </circle>
              )}
            </svg>
          </div>
          {/* Title */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <div style={{
                fontSize: 15, fontWeight: 800, letterSpacing: "0.04em",
                background: "linear-gradient(135deg, #c4b5fd 0%, #8b5cf6 50%, #6366f1 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}>CORTEX</div>
              {/* "-beta" renders as a single accent-purple β with a hair of space after the
                  number; whiteSpace keeps the badge on one line (package.json keeps -beta). */}
              <span style={{ fontSize: 9, fontWeight: 600, color: C.subtle, letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                {`v${FULL_VERSION.replace(/-beta$/, "")}`}
                {FULL_VERSION.endsWith("-beta") && (
                  <span style={{ color: C.accentText, fontWeight: 700, marginLeft: 2 }}>β</span>
                )}
              </span>
            </div>
            <div style={{ fontSize: 9, color: C.subtle, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 2, fontWeight: 500 }}>By Social IQ</div>
          </div>
        </div>
      </div>

      <nav style={{ padding:"10px", flex:1, display:"flex", flexDirection:"column" }}>
        {navItems.map(({ label, icon, view: v, beta }) => (
          <div key={label}>
            <button className={`ni ${view === v ? "on" : ""}`} onClick={() => onNavigate(v)}>
              <Ic n={icon} s={14} />{label}
            {beta && <span style={{ marginLeft: "auto", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 6px", borderRadius: 5, color: C.accentText, background: C.accentLight, border: `1px solid ${C.accentBorder}` }}>Beta</span>}
            </button>
            {/* SOC-792: Brand Campaigns is its own page under Launcher, so it needs a way in. */}
            {v === "launcher" && view === "launcher" && (
              <div style={{ display:"flex", flexDirection:"column", paddingLeft:22 }}>
                {([["Creator Ads", "/launcher"], ["Brand Campaigns", "/launcher/brands"]] as [string, string][]).map(([l, p]) => (
                  <button key={p} className={`ni ${pathname === p ? "on" : ""}`} style={{ padding:"5px 12px", fontSize:12 }} onClick={() => navigate(p)}>{l}</button>
                ))}
              </div>
            )}
          </div>
        ))}
        {canView?.("invoices") && (
          <button className={`ni ${view === "invoices" ? "on" : ""}`} onClick={() => onNavigate("invoices")}>
            <Ic n="dollar" s={14} />Invoices
          </button>
        )}
        {isAdmin && (
          <button className={`ni ${view === "admin" ? "on" : ""}`} onClick={() => onNavigate("admin")}>
            <Ic n="users" s={14} />Admin
          </button>
        )}

        {/* Light / dark theme toggle — pinned to the bottom of the nav */}
        <div style={{ display:"flex", gap:4, marginTop:"auto", paddingTop:10 }}>
          {([["light","☀","Light"],["dark","🌙","Dark"]] as [ "light"|"dark", string, string ][]).map(([mode, icon, label]) => {
            const on = (mode === "dark") === dark;
            return (
              <button key={mode} onClick={() => { if ((mode === "dark") !== dark) toggle(); }}
                title={`${label} mode`}
                style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:5,
                         padding:"6px 8px", borderRadius:7, cursor:"pointer", fontFamily:"inherit",
                         fontSize:11, fontWeight:600, transition:"all 0.15s",
                         border:`1px solid ${on ? C.accentBorder : C.border}`,
                         background: on ? C.accentLight : "transparent",
                         color: on ? C.accentText : C.muted }}>
                <span style={{ fontSize:12 }}>{icon}</span>{label}
              </button>
            );
          })}
        </div>
      </nav>

      <div style={{ padding:"10px", borderTop:`1px solid ${C.border}` }}>
        <div style={{ fontSize:11, color:C.faint, padding:"0 4px", marginBottom:8, fontStyle:"italic", lineHeight:1.6 }}>
          "{BEAST_QUOTES[quoteIdx]}"
        </div>
        {currentUser && (
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 12px", marginBottom:4 }}>
            <div style={{ width:22, height:22, borderRadius:"50%", background:`linear-gradient(135deg,${C.accent},#1a6e8a)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:"#fff", flexShrink:0 }}>
              {currentUser[0].toUpperCase()}
            </div>
            <span style={{ fontSize:12, color:C.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{currentUser}</span>
            {onSignOut && (
              <button
                onClick={onSignOut}
                title="Sign out"
                style={{ background:"transparent", border:"none", color:C.subtle, cursor:"pointer", fontSize:11, padding:"2px 4px", fontFamily:"inherit", flexShrink:0 }}
                onMouseEnter={e => (e.currentTarget.style.color = C.text)}
                onMouseLeave={e => (e.currentTarget.style.color = C.subtle)}
              >
                Sign out
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
};
